import axios from 'axios';

const BASE = (process.env.REACT_APP_EZTS_API_BASE_URL || '').replace(/\/+$/, '');

/** Auth header comes from axios interceptor (ensureAccessToken / refresh). */

export function getCurrentYearDateRange() {
  const year = new Date().getFullYear();
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

function filterTimesheetsInRange(list, from, to) {
  if (!from && !to) return list;
  return list.filter((ts) => {
    const date = String(ts?.dateFrom ?? ts?.fromDate ?? '').slice(0, 10);
    if (!date) return false;
    return (!from || date >= from) && (!to || date <= to);
  });
}

function unwrapPayload(data) {
  if (data == null) return data;
  if (typeof data === 'object' && 'data' in data && data.data != null) {
    return unwrapPayload(data.data);
  }
  return data;
}

export function normalizeTimesheetCounts(data) {
  const empty = { draft: 0, pending: 0, rejected: 0 };

  if (data == null) return empty;

  const pickNumber = (obj, ...keys) => {
    for (const key of keys) {
      const val = obj[key];
      if (typeof val === 'number' && !Number.isNaN(val)) return val;
    }
    return null;
  };

  if (typeof data === 'object' && !Array.isArray(data)) {
    if (Array.isArray(data.counts)) {
      return normalizeTimesheetCounts(data.counts);
    }

    return {
      draft: pickNumber(data, 'draft', 'draftCount', 'drafts', 1, '1') ?? 0,
      pending: pickNumber(data, 'pending', 'pendingCount', 'pendings', 2, '2') ?? 0,
      rejected: pickNumber(data, 'rejected', 'rejectedCount', 'rejecteds', 4, '4') ?? 0,
    };
  }

  if (Array.isArray(data)) {
    const result = { ...empty };
    data.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const status = item.status ?? item.statusId ?? item.statusCode;
      const count = item.count ?? item.total ?? item.value;
      if (typeof count !== 'number') return;
      const statusMap = { 1: 'draft', 2: 'pending', 4: 'rejected', draft: 'draft', pending: 'pending', rejected: 'rejected' };
      const key = statusMap[status] ?? statusMap[String(status).toLowerCase()];
      if (key && key in result) result[key] = count;
    });
    return result;
  }

  return empty;
}

export const TIMESHEET_STATUS_MAP = {
  1: 'draft',
  2: 'pending',
  3: 'approved',
  4: 'rejected',
};

export function normalizeTimesheet(ts) {
  const status =
    TIMESHEET_STATUS_MAP[ts?.status] ??
    String(ts?.status ?? 'unknown').toLowerCase();

  const approverName = ts?.approver
    ? `${ts.approver.firstName || ''} ${ts.approver.lastName || ''}`.trim()
    : '';

  const project =
    ts?.project?.projectCode ||
    ts?.project?.projectName ||
    ts?.user?.projects?.[0]?.projectCode ||
    '-';

  const standardTime = Number(ts?.stSubtotal ?? 0);
  const overtime = Number(ts?.otSubtotal ?? 0);
  const total = Number(ts?.total ?? standardTime + overtime);

  return {
    id: ts?.tsId ?? ts?.id,
    status,
    fromDate: ts?.dateFrom ?? '',
    toDate: ts?.dateTo ?? '',
    approver: approverName,
    project,
    standardTime,
    overtime,
    total,
    _raw: ts,
  };
}

const TIMESHEET_COUNT_STATUS_MAP = { 1: 'draft', 2: 'pending', 4: 'rejected' };

export function countTimesheetsByStatus(list) {
  const counts = { draft: 0, pending: 0, rejected: 0 };
  const items = Array.isArray(list) ? list : [];

  items.forEach((ts) => {
    const raw = ts?.status ?? ts?.statusId;
    const key =
      typeof raw === 'number'
        ? TIMESHEET_COUNT_STATUS_MAP[raw]
        : TIMESHEET_COUNT_STATUS_MAP[Number(raw)] ?? String(raw || '').toLowerCase();
    if (key && key in counts) counts[key] += 1;
  });

  return counts;
}

export async function getTimesheets({
  orgId = 1,
  userId,
  from = '',
  to = '',
  projectId = '',
} = {}) {
  if (!userId) throw new Error('Missing userId');

  const url = `${BASE}/timesheet/all`;

  const res = await axios.get(url, {
    params: { userId, projectId, from, to, orgId },
  });

  const payload = unwrapPayload(res.data);
  const list = Array.isArray(payload) ? payload : [];
  return list.map(normalizeTimesheet);
}

export async function getTimesheetCount({ orgId = 1, userId, from, to } = {}) {
  if (!userId) throw new Error('Missing userId');

  const range = getCurrentYearDateRange();
  const url = `${BASE}/timesheet/count`;

  const res = await axios.get(url, {
    params: {
      orgId,
      userId,
      from: from ?? range.from,
      to: to ?? range.to,
    },
  });

  return normalizeTimesheetCounts(unwrapPayload(res.data));
}

export async function getTimesheetCountsFromList({ orgId = 1, userId, from, to } = {}) {
  if (!userId) throw new Error('Missing userId');

  const range = getCurrentYearDateRange();
  const url = `${BASE}/timesheet/all`;

  const res = await axios.get(url, {
    params: {
      userId,
      projectId: '',
      from: from ?? range.from,
      to: to ?? range.to,
      orgId,
    },
  });

  const payload = unwrapPayload(res.data);
  const list = Array.isArray(payload) ? payload : [];
  const rangeFrom = from ?? range.from;
  const rangeTo = to ?? range.to;
  return countTimesheetsByStatus(filterTimesheetsInRange(list, rangeFrom, rangeTo));
}
